import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import decryptData from "../../../decrypt";
import { handleLogout } from "../../../config/axios";

const isMobileDevice = () => {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.matchMedia && window.matchMedia("(max-width: 768px)").matches)
  );
};

const downloadPdfWithMobileSupport = (blob: Blob, fileName: string) => {
  const blobUrl = URL.createObjectURL(blob);
  if (isMobileDevice()) {
    const newWindow = window.open(blobUrl, "_blank");
    if (!newWindow) {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      const clickEvent = new MouseEvent("click", { view: window, bubbles: true, cancelable: true });
      link.dispatchEvent(clickEvent);
      setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(blobUrl); }, 100);
    } else {
      setTimeout(() => { URL.revokeObjectURL(blobUrl); }, 1000);
    }
  } else {
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(blobUrl); }, 100);
  }
};

interface ExportFilters {
  agenti: string[];
  stato_cliente?: string;
  codice_cliente?: string;
}

export const saveAnagraficaPdf = async (filters: ExportFilters) => {
  const token = localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  // 1. Fetch all customers
  const utentiRes = await fetch("/nodeapi/utenti", {
    method: "POST",
    headers,
    body: JSON.stringify({
      agenti: filters.agenti,
      page: 1,
      pageSize: 100000,
      stato_cliente: filters.stato_cliente || "Tutti",
      codice_cliente: filters.codice_cliente || null,
    }),
  });
  if (utentiRes.status === 401 || utentiRes.status === 403 || utentiRes.status === 500) {
    handleLogout();
    return;
  }
  const utentiResult = await utentiRes.json();
  const utentiDecrypted = decryptData(utentiResult.encryptedData, utentiResult.encryptedKey, utentiResult.iv);
  const { data: customers } = JSON.parse(utentiDecrypted);

  if (!customers || customers.length === 0) {
    console.warn("Nessun cliente disponibile per l'esportazione.");
    return;
  }

  // 2. Fetch all customer_detail (pivoted)
  const detailRes = await fetch("/nodeapi/customer-detail/export", { method: "GET", headers });
  const detailResult = await detailRes.json();
  const detailDecrypted = decryptData(detailResult.encryptedData, detailResult.encryptedKey, detailResult.iv);
  const detailRows: Record<string, string>[] = JSON.parse(detailDecrypted);

  const detailMap = new Map<string, Record<string, string>>();
  for (const row of detailRows) {
    const { customer_id, ...attrs } = row;
    detailMap.set(customer_id, attrs);
  }

  const detailKeys = Array.from(
    detailRows.reduce((acc, row) => {
      Object.keys(row).filter(k => k !== "customer_id").forEach(k => acc.add(k));
      return acc;
    }, new Set<string>())
  );

  // 3. Build table
  const baseKeys = ["CODICE_CLIENTE", "RAGIONE_SOCIALE", "STATO_CLIENTE"];
  const allKeys = [...baseKeys, ...detailKeys];
  const tableHead = allKeys.map(k => k.replace(/_/g, " ").toUpperCase());
  const tableRows = customers.map((c: Record<string, string>) => {
    const detail = detailMap.get(c.CODICE_CLIENTE) ?? {};
    return [
      ...baseKeys.map(k => c[k] ?? ""),
      ...detailKeys.map(k => detail[k] ?? ""),
    ];
  });

  // 4. Generate PDF
  const doc = new jsPDF("landscape", "in", "a4");
  const marginLeft = 0.5;
  const marginTop = 0.5;

  doc.addImage("/assets/cedi.png", "PNG", marginLeft, marginTop, 0.5, 0.5);

  doc.setFontSize(10);
  const legalX = marginLeft + 0.5 + 0.2;
  doc.text("Sede Legale", legalX, marginTop + 0.2);
  doc.text("Via Luca da Penne, 3", legalX, marginTop + 0.35);
  doc.text("80122 - NAPOLI", legalX, marginTop + 0.5);
  doc.text("C.F: 04805240639 - P.I. 04805240639", legalX, marginTop + 0.65);
  doc.text("REA Napoli n. 396782", legalX, marginTop + 0.8);

  const titleY = marginTop + 1.2;
  doc.setFontSize(12);
  doc.text(`ANAGRAFICA CLIENTI - DETTAGLIO COMMERCIALE ${moment().format("DD/MM/YYYY")}`, marginLeft, titleY);

  let pageNumber = 1;
  autoTable(doc, {
    startY: titleY + 0.3,
    head: [tableHead],
    body: tableRows,
    styles: { fontSize: 7, cellPadding: 0.07, lineColor: [221, 221, 221], lineWidth: 0.01 },
    headStyles: { fillColor: [248, 248, 248], textColor: [0, 0, 0], fontStyle: "bold" },
    margin: { left: marginLeft, right: marginLeft },
    didDrawPage: (dataArg) => {
      if (dataArg.pageNumber > pageNumber) pageNumber = dataArg.pageNumber;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.text(`Pagina ${pageNumber}`, pageWidth - marginLeft, pageHeight - 0.3, { align: "right" });
    },
  });

  downloadPdfWithMobileSupport(doc.output("blob"), `anagrafica_clienti_${moment().format("DDMMYYYY")}.pdf`);
};
